import React, { useEffect, useState } from "react";
import { Card, Badge, Button, EmptyState, LoadingState, Field, Input } from "@/components/design-system";
import { adminQueue, adminApprove, adminReject } from "@/lib/ambassadors";
import { formatApiError } from "@/lib/api";

const cn = (...xs) => xs.filter(Boolean).join(" ");
const TABS = ["scoring", "scored", "approved", "rejected"];

export default function AdminAmbassadors() {
  const [tab, setTab] = useState("scored");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(null);

  const load = async () => { setLoading(true); try { setItems(await adminQueue(tab)); } catch (e) { alert(formatApiError(e)); } finally { setLoading(false); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const decide = async (id, action) => {
    setBusy(id);
    try {
      if (action === "approve") await adminApprove(id, notes[id]); else await adminReject(id, notes[id]);
      setItems((xs) => xs.filter((x) => x.id !== id));
    } catch (e) { alert(formatApiError(e)); } finally { setBusy(null); }
  };

  return (
    <div data-testid="admin-ambassadors-page">
      <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Administrator</p>
      <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">Ambassador applications</h1>
      <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">Every application arrives with a GPT-5.6 score + rationale. AI recommends — you approve.</p>
      <div className="mt-6 inline-flex items-center gap-1 rounded-full border border-[#0B1F3A]/10 p-1 bg-white">
        {TABS.map((t) => (
          <button key={t} data-testid={`ambassador-tab-${t}`} onClick={() => setTab(t)} className={cn("px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider", tab === t ? "bg-[#0B1F3A] text-white" : "text-[#0B1F3A]/70")}>{t}</button>
        ))}
      </div>
      <div className="mt-6 space-y-4">
        {loading && <LoadingState />}
        {!loading && items.length === 0 && <EmptyState title="Nothing here" description={`No applications in "${tab}".`} />}
        {!loading && items.map((a) => (
          <Card key={a.id} data-testid={`ambassador-app-${a.id}`} className="!p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[#0B1F3A]">{a.applicant_name}<span className="text-[#0B1F3A]/50 ml-2">{a.applicant_email}</span></div>
                <div className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/60 mt-1">rep {a.reputation_at_apply} · {a.tier_at_apply}</div>
                <p className="mt-3 text-sm text-[#0B1F3A]/85"><b>Motivation:</b> {a.motivation}</p>
                <p className="mt-2 text-sm text-[#0B1F3A]/85"><b>Audience:</b> {a.audience}</p>
                <p className="mt-1 text-sm text-[#0B1F3A]/85"><b>Channels:</b> {a.channels}</p>
                {a.links?.length > 0 && <p className="mt-1 text-xs text-[#2D7FF9]">{a.links.join(" · ")}</p>}
              </div>
              {typeof a.ai_score === "number" && (
                <div className="text-right">
                  <div className="font-[Montserrat] font-extrabold text-4xl text-[#0B1F3A]">{a.ai_score}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/60">AI trust score</div>
                </div>
              )}
            </div>
            {a.ai_score_rationale && (
              <div className="mt-4 pt-4 border-t border-[#0B1F3A]/10 text-sm text-[#0B1F3A]/85"><b>AI:</b> {a.ai_score_rationale}</div>
            )}
            {(tab === "scored") && (
              <div className="mt-4 flex flex-wrap items-end gap-3">
                <Field label="Notes (optional)"><Input value={notes[a.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [a.id]: e.target.value }))} maxLength={280} /></Field>
                <Button data-testid={`ambassador-approve-${a.id}`} variant="primary" disabled={busy === a.id} onClick={() => decide(a.id, "approve")}>Approve</Button>
                <Button data-testid={`ambassador-reject-${a.id}`} variant="danger" disabled={busy === a.id} onClick={() => decide(a.id, "reject")}>Reject</Button>
              </div>
            )}
            {a.status === "approved" && <div className="mt-3 text-xs text-[#0B1F3A]/60">Approved {a.decided_at ? new Date(a.decided_at).toLocaleDateString() : ""} · {a.admin_notes}</div>}
            {a.status === "rejected" && <div className="mt-3 text-xs text-red-700">{a.admin_notes}</div>}
          </Card>
        ))}
      </div>
    </div>
  );
}
